import { BeatSequencer } from '@/story/sequencer';
import { IT_FAILS_BEAT, IT_FAILS_CUES } from '@/story/act3/itFails';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';
import type { Framing } from '@/camera/framing';
import { TIMBER_PATCHED_BAY, YARD } from '@/world/loadingYard';
import { GATE_SWING_FROM, GATE_SWING_SECONDS, IT_FAILS_OUTSIDE, KESTNER_JAMMED, KESTNER_START, SURGE_BAY_APPROACH } from '@/world/yardFailsStaging';

/**
 * Beat 13 part 1: the story, and the beat's three authored framings
 * (`docs/beat-13-plan.md`). Everything else is the rig's.
 *
 * **One look at him** (the plan: *"the camera finds him once… never closer
 * than 4 m"*): from her side of the gateway, over the swing, held from just
 * before he moves the leaf until it has stopped. It is the beat's only
 * authored framing before the surge; the whistle and the bell are the rig's,
 * because the bell is nothing new (S-1: no new camera language for it).
 */
const KESTNER_LOOK_FROM = 0.8;
const KESTNER_LOOK_UNTIL = GATE_SWING_FROM + GATE_SWING_SECONDS + 1.6;
const KESTNER_MID = { x: (KESTNER_START.x + KESTNER_JAMMED.x) / 2, z: (KESTNER_START.z + KESTNER_JAMMED.z) / 2 };
/**
 * South-east of him, into the yard: the herd's jammed head and the leaf between. 4.6 m.
 *
 * Portrait (D-246): the same lens, aimed a quarter of the way toward where he
 * starts and opened to 58°. Unchanged, the narrow frame cut him in half as the
 * swing began (head at NDC x 0.77); this spreads his walk across the frame
 * (0.51 → −0.55) and keeps more of the stalled herd in.
 */
export const KESTNER_SHOT: Framing = {
  position: { x: KESTNER_MID.x + 3.4, y: 1.45, z: KESTNER_MID.z - 3.1 },
  lookAt: { x: KESTNER_MID.x, y: 1.05, z: KESTNER_MID.z + 0.3 },
  fov: 48,
  portrait: {
    position: { x: KESTNER_MID.x + 3.4, y: 1.45, z: KESTNER_MID.z - 3.1 },
    lookAt: { x: KESTNER_MID.x + 0.25 * (KESTNER_START.x - KESTNER_MID.x), y: 1.05, z: KESTNER_MID.z + 0.3 + 0.25 * (KESTNER_START.z - KESTNER_MID.z) },
    fov: 58,
  },
};

/**
 * The surge (D-095: a shot, not a system): low, at leg height, from the
 * hedgerow side of the fence, looking back through the wire at her being
 * pressed along it with the pack's legs and flanks behind her. It tracks her
 * along the fence, so it is the same shot from wherever she stood.
 *
 * Outside the fence because inside it there is nowhere to put a lens: the
 * two versions that followed her from behind and from ahead were both inside
 * a pig for the whole shot — the pack is packed, which is the point.
 */
const SURGE_HEIGHT = 0.4;
const SURGE_OUTSIDE_BY = 0.75;

/** The hold: the hedgerow side, at her height, looking back at the gap and the snouts in it. */
export const OUTSIDE_SHOT: Framing = {
  // West of the paddock's fence line (x 3.6): the first frame put the lens
  // against it and a post filled the right third.
  position: { x: TIMBER_PATCHED_BAY.x + 0.45, y: 0.3, z: IT_FAILS_OUTSIDE.z + 1.2 },
  lookAt: { x: TIMBER_PATCHED_BAY.x, y: 0.28, z: YARD.maxZ },
  fov: 50,
};

const BLACK_HOLD_FADE = 0.5;

function createItFailsPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const story = new BeatSequencer(IT_FAILS_BEAT);
  const fade = capabilities.createFade(IT_FAILS_CUES);
  // Copied out of the borrowed tick record (`BeatPartTick`); the surge shot follows it.
  const her = { x: 0, y: 0, z: 0 };
  const surgeShot: Framing = { position: { x: 0, y: SURGE_HEIGHT, z: 0 }, lookAt: { x: 0, y: 0.3, z: 0 }, fov: 62 };
  let stage: 'the-jam' | 'the-whistle' | 'the-bell' | 'the-surge' | 'outside' | 'close' | null = null;
  let stageTime = 0;
  let disposed = false;

  return {
    story,
    interactables: [],
    get fade(): number { return fade.fade; },
    // From the surge on, the beat has the frame and her body; nothing is offered.
    get promptSuppressed(): boolean {
      return !disposed && (stage === 'the-surge' || stage === 'outside' || stage === 'close' || fade.fade > BLACK_HOLD_FADE);
    },
    fixedStep({ dt, player }): void {
      her.x = player.x;
      her.y = player.y;
      her.z = player.z;
      stageTime += dt;
    },
    handleCue(cue): void {
      const next = cue.id === IT_FAILS_CUES.jam ? 'the-jam'
        : cue.id === IT_FAILS_CUES.whistle ? 'the-whistle'
          : cue.id === IT_FAILS_CUES.bell ? 'the-bell'
            : cue.id === IT_FAILS_CUES.surge ? 'the-surge'
              : cue.id === IT_FAILS_CUES.outside ? 'outside'
                : cue.id === IT_FAILS_CUES.fadeOut ? 'close' : null;
      if (next !== null) {
        stage = next;
        stageTime = 0;
      }
      fade.handle(cue);
    },
    renderStep(frameDt): void { if (!disposed) fade.update(frameDt); },
    framing(): Framing | null {
      if (stage === 'the-jam') return stageTime >= KESTNER_LOOK_FROM && stageTime < KESTNER_LOOK_UNTIL ? KESTNER_SHOT : null;
      if (stage === 'the-surge') {
        // A step west of her, toward the bay she is being pushed at, so she
        // comes along the fence into the frame rather than out of it.
        surgeShot.position.x = Math.max(SURGE_BAY_APPROACH.x - 1.6, her.x - 1.1);
        surgeShot.position.z = YARD.maxZ + SURGE_OUTSIDE_BY;
        surgeShot.lookAt.x = her.x;
        surgeShot.lookAt.z = Math.min(her.z, YARD.maxZ - 0.3);
        return surgeShot;
      }
      if (stage === 'outside' || stage === 'close') return OUTSIDE_SHOT;
      return null;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      fade.dispose();
    },
  };
}

export const IT_FAILS_PART_MODULE: BeatPartModuleDefinition = {
  beat: 13,
  part: 1,
  room: 'yard-fails',
  create: createItFailsPartModule,
};
